'use client'

import { createContext, useContext, useEffect, useState } from 'react'
import { log, warn } from '@/lib/logger'
import { useWallet } from '@solana/wallet-adapter-react'
import { AuthService, type AuthSession } from '@/services/AuthService'

type Session = any

export type Web3Profile = {
  id: string
  wallet_address: string
  player_pda?: string
  active_character_slot?: number
  character_cnft_1?: string
  character_cnft_2?: string
  character_cnft_3?: string
  character_cnft_4?: string
  character_cnft_5?: string
}

export type AuthStore = {
  session: Session | null
  web3Profile: Web3Profile | null
  isWeb3User: boolean
}

export const AuthContext = createContext<AuthStore | undefined>(undefined)

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [backendSession, setBackendSession] = useState<AuthSession | null>(null)
  const [web3Profile, setWeb3Profile] = useState<Web3Profile | null>(null)
  const [lastChecked, setLastChecked] = useState<number>(0)

  // Create compatible session object for backend auth (computed during render)
  const compatibleSession: Session | null = backendSession ? {
    user: {
      id: backendSession.user.id,
      email: backendSession.user.email,
      userMetadata: {
        username: backendSession.user.username,
      },
    } as any,
    expires_at: Date.now() / 1000 + 7 * 24 * 60 * 60, // 7 days
  } as any : null

  // Safely get wallet state with fallback
  let connected = false
  let _publicKey = null

  try {
    const wallet = useWallet()
    connected = wallet.connected
    _publicKey = wallet.publicKey
  } catch (error) {
    // Wallet context not available yet, use defaults
    log('Wallet context not available yet')
  }

  // Check for existing Web3 profile when wallet connects OR when session exists
  useEffect(() => {
    // CRITICAL: Do NOT run ANY wallet profile checks if user is not logged in
    const sessionUser = backendSession?.user ? { id: backendSession.user.id } : null
    if (!sessionUser) {
      return; // No session - user hasn't logged in yet, skip entire effect
    }

    const checkWeb3Profile = async () => {
      // If we have a session but no web3Profile yet, check if this session corresponds to a Web3 user
      const userMeta = (backendSession?.user as any)?.user_metadata;
      if (!web3Profile && userMeta?.is_web3_user) {
        // Look up profile by user ID from session
        try {
          // Use backend API
          const { fetchProfile } = await import('@/lib/backend-api');
          const result = await fetchProfile({ userId: sessionUser.id });
          const profile = result.profile;
          if (profile && profile.wallet_address) {
            setWeb3Profile(profile);
            return;
          }
        } catch (error) {
          // Silent fail - not critical
        }
      }

      // Skip ALL wallet-based profile checks for username/password login users
      // Only check wallet profiles for Web3 users
      // CRITICAL: Double-check session exists and has user before making any calls
      if (!backendSession?.user || (backendSession.user as any).user_metadata?.is_web3_user !== true) {
        // No session OR username/password user - skip all wallet checks
        return
      }

      // Only check wallet-based profile if wallet is actually connected
      if (!connected || !_publicKey) {
        return
      }

      // Prevent too frequent checks (max once every 2 seconds)
      const now = Date.now()
      if (now - lastChecked < 2000) {
        return
      }
      setLastChecked(now)

      // CRITICAL: Final check - only make API call if we have a valid Web3 user session
      if (!backendSession?.user || (backendSession.user as any).user_metadata?.is_web3_user !== true) {
        return
      }

      try {
        const backendUrl = import.meta.env.VITE_BACKEND_URL || 'https://obelisk-skiller-production.up.railway.app';
        const response = await fetch(`${backendUrl}/api/profiles`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            walletAddress: _publicKey.toBase58()
          }),
        })

        if (response.ok) {
          const data = await response.json()
          if (data.profile) {
            setWeb3Profile(data.profile)
          } else {
            // Don't clear if we have a session-based profile
            if (!(backendSession?.user as any)?.user_metadata?.is_web3_user) {
              setWeb3Profile(null)
            }
          }
        }
      } catch (error) {
        // Silent fail - not critical for wallet checks
      }
    }

    // CRITICAL: Only run checkWeb3Profile if user is logged in
    // This prevents /api/profile calls on the login page
    if (backendSession?.user) {
      checkWeb3Profile()
    }
  }, [connected, _publicKey, web3Profile, backendSession, lastChecked])

  // Backend auth session management
  useEffect(() => {
    const refreshSession = async () => {
      try {
        const session = await AuthService.getSession()
        setBackendSession(session)
      } catch {
        setBackendSession(null)
      }
    }

    // Listen for auth state changes FIRST (before checking session)
    // This ensures we catch events that fire immediately after signup/login
    const handleAuthStateChanged = () => {
      refreshSession()
    }
    window.addEventListener('auth-state-changed', handleAuthStateChanged)

    // Then check for existing session on mount
    refreshSession()

    // Poll for session changes (or use WebSocket in future)
    const interval = setInterval(() => {
      refreshSession()
    }, 30000) // Every 30s

    return () => {
      clearInterval(interval)
      window.removeEventListener('auth-state-changed', handleAuthStateChanged)
    }
  }, [])

  // Auto-initialize PDA for Web2 users if they don't have one
  const checkAndInitializeWeb2Player = async (_userId: string, email: string | undefined) => {
    try {
      log('🔍 AUTH: Checking if Web2 user needs PDA initialization:', {
        _userId: _userId.slice(0, 8) + '...',
        email
      })

      // Get user profile to check if they already have a PDA
      let profile: any = null;

      // Use backend API
      const { fetchProfile } = await import('@/lib/backend-api');
      try {
        const result = await fetchProfile({ userId: _userId });
        profile = result.profile;
      } catch (err) {
        console.warn('Failed to fetch profile from backend:', err);
      }

      log('📋 AUTH: Profile check result:', {
        profile,
        hasPDA: !!profile?.player_pda
      })

      // Check if player account already exists
      if (profile?.id) {
        log('✅ AUTH: User already has account:', profile.id)
        return
      }

      log('🚀 AUTH: Auto-initializing player account for new user...')

      // Initialize Web2 player automatically
      const backendUrl = import.meta.env.VITE_BACKEND_URL || 'https://obelisk-skiller-production.up.railway.app';
      const token = localStorage.getItem('runara_auth_token');
      const headers: HeadersInit = { 'Content-Type': 'application/json' };
      if (token) headers['Authorization'] = `Bearer ${token}`;

      // Use unified initialization endpoint (no PDA required, no Web2/Web3 distinction)
      const response = await fetch(`${backendUrl}/api/players/initialize`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          name: 'Player', // Default name
          _characterClass: 0,
        })
      })

      log('🌐 AUTH: Initialize-web2 response status:', response.status)

      if (response.ok) {
        const result = await response.json()
        log('✅ AUTH: Auto-initialized player account (off-chain):', result.player?.id || result.playerPda)
      } else {
        const errorText = await response.text().catch(() => '')
        warn('⚠️ AUTH: Failed to auto-initialize PDA:', {
          status: response.status,
          statusText: response.statusText,
          body: errorText,
        })
      }
    } catch (error) {
      console.error('❌ AUTH: Error during auto-initialization:', error)
    }
  }

  // Derive isWeb3User from profile data — true if the profile has a wallet_address or user_type is WEB3
  const isWeb3User = !!(
    web3Profile?.wallet_address ||
    backendSession?.profile?.wallet_address ||
    backendSession?.profile?.user_type === 'WEB3'
  )

  // Make auth data globally available for Phaser scenes
  useEffect(() => {
    (window as any).web3Profile = web3Profile;
    (window as any).isWeb3User = isWeb3User;

    // Set current user ID for multiplayer
    if (backendSession?.user?.id) {
      (window as any).currentUserId = backendSession.user.id;
      // Set currentUserId for multiplayer (silent - no logging needed)
    } else {
      (window as any).currentUserId = null;
    }
  }, [web3Profile, isWeb3User, backendSession])

  return (
    <AuthContext.Provider value={{
      session: compatibleSession,
      web3Profile: web3Profile,
      isWeb3User: isWeb3User
    }}>
      {children}
    </AuthContext.Provider>
  )
}

// Keep useSupabase for backward compatibility, but it now returns AuthStore
export const useSupabase = () => {
  const context = useContext(AuthContext)

  if (context === undefined) {
    throw new Error('useSupabase must be used within an AuthProvider')
  }

  // Return with supabase: null for backward compatibility
  return {
    ...context,
    supabase: null as any
  }
}